import { useForm } from '@tanstack/react-form';
import { log } from 'evlog/client';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router';
import { z } from 'zod';

import {
  Button,
  CardContent,
  Checkbox,
  Input,
  Label,
} from '@virtual-care/shadcn';
import {
  AuthLayout,
  BirthDateField,
  FieldMessage,
  FormMessage,
  GenderField,
  Star,
  client,
  errorToFormIssue,
} from '@virtual-care/ui';

const nameValidation = z
  .string()
  .min(1, { message: 'shared.validation.nameRequired' })
  .regex(/^[\p{L}\s]+$/u, {
    message: 'shared.validation.nameCharactersOnly',
  });

const schema = z.object({
  email: z.string().email({ message: 'shared.validation.emailInvalid' }),
  lastName: nameValidation,
  parentName: nameValidation.optional().or(z.literal('')),
  firstName: nameValidation,
  birthDate: z
    .string()
    .min(1, { message: 'shared.validation.birthDateRequired' }),
  gender: z.string().min(1, { message: 'shared.validation.genderRequired' }),
  nationalId: z.string().optional(),
  terms: z
    .boolean({
      errorMap: () => ({
        message: 'shared.validation.termsAcknowledgeRequired',
      }),
    })
    .superRefine((value, ctx) => {
      if (value !== true) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          message: 'shared.validation.termsAcknowledgeRequired',
        });
      }
    }),
});

export default function CompleteProfile() {
  const navigate = useNavigate();
  const { t } = useTranslation();

  const form = useForm({
    defaultValues: {
      lastName: '',
      firstName: '',
      birthDate: '',
      gender: '',
      nationalId: undefined,
      terms: false,
      parentName: undefined,
      email: '',
    } as z.infer<typeof schema>,
    validators: {
      onSubmit: schema,
      onSubmitAsync: async ({ value }) => {
        try {
          log.info({ action: 'complete_profile' });
          await client.request('POST /profile', {
            email: value.email,
            birthDate: value.birthDate,
            firstName: value.firstName,
            gender: value.gender,
            lastName: value.lastName,
            nationalId: value.nationalId,
            parentName: value.parentName,
          });
          await navigate('/');
          return void 0;
        } catch (error) {
          log.error({ action: 'complete_profile', error });
          return errorToFormIssue(error);
        }
      },
    },
  });

  return (
    <AuthLayout
      title={t('auth.completeProfile.title')}
      subtitle={t('auth.completeProfile.subtitle')}
      center={true}
    >
      <CardContent>
        <form
          className="space-y-4"
          onSubmit={async (e) => {
            e.preventDefault();
            e.stopPropagation();
            await form.handleSubmit();
          }}
        >
          <div className="grid grid-cols-3 gap-x-4">
            <form.Field
              name="firstName"
              children={(field) => (
                <div className="grid w-full items-center gap-2">
                  <Label htmlFor="firstName" className="text-sm">
                    {t('auth.completeProfile.firstName')}
                    <Star />
                  </Label>
                  <Input
                    id="firstName"
                    value={field.state.value}
                    onBlur={field.handleBlur}
                    onChange={(e) => field.handleChange(e.target.value)}
                  />
                  <FieldMessage errors={[field.state.meta.errors[0]]} />
                </div>
              )}
            />
            <form.Field
              name="parentName"
              children={(field) => (
                <div className="grid w-full items-center gap-2">
                  <Label
                    htmlFor="parentName"
                    className="text-muted-foreground text-sm"
                  >
                    {t('auth.completeProfile.parentName')}
                  </Label>
                  <Input
                    id="parentName"
                    value={field.state.value}
                    onBlur={field.handleBlur}
                    onChange={(e) => field.handleChange(e.target.value)}
                  />
                  <FieldMessage errors={[field.state.meta.errors[0]]} />
                </div>
              )}
            />
            <form.Field
              name="lastName"
              children={(field) => (
                <div className="grid w-full items-center gap-2">
                  <Label htmlFor="lastName" className="text-sm">
                    {t('auth.completeProfile.lastName')}
                    <Star />
                  </Label>
                  <Input
                    id="lastName"
                    value={field.state.value}
                    onBlur={field.handleBlur}
                    onChange={(e) => field.handleChange(e.target.value)}
                  />
                  <FieldMessage errors={[field.state.meta.errors[0]]} />
                </div>
              )}
            />
          </div>
          <form.Field
            name="birthDate"
            children={(field) => <BirthDateField field={field} star />}
          />

          <form.Field
            name="gender"
            children={(field) => (
              <GenderField
                field={field}
                star={true}
                label={t('common.gender')}
                placeholder={t('common.select')}
                maleLabel={t('common.male')}
                femaleLabel={t('common.female')}
              />
            )}
          />

          <form.Field
            name="nationalId"
            children={(field) => (
              <div className="grid w-full items-center gap-2">
                <Label htmlFor="nationalId" className="text-sm">
                  {t('auth.completeProfile.nationalId')}
                </Label>
                <Input
                  id="nationalId"
                  value={field.state.value}
                  onBlur={field.handleBlur}
                  onChange={(e) => field.handleChange(e.target.value)}
                />
                <FieldMessage errors={field.state.meta.errors} />
              </div>
            )}
          />

          <form.Field
            name="email"
            children={(field) => (
              <div className="grid w-full items-center gap-2">
                <Label className="text-sm" htmlFor="email">
                  {t('auth.common.email')}
                  <Star />
                </Label>
                <Input
                  id="email"
                  dir="ltr"
                  autoComplete="email"
                  type="email"
                  value={field.state.value}
                  onBlur={field.handleBlur}
                  onChange={(e) => field.handleChange(e.target.value)}
                />
                <FieldMessage errors={field.state.meta.errors} />
              </div>
            )}
          />
          <form.Field
            name="terms"
            validators={{
              onChange: schema.shape.terms,
            }}
            children={(field) => (
              <div className="grid w-full items-center gap-2">
                <div className="flex items-start space-x-2">
                  <Checkbox
                    id="terms"
                    checked={field.state.value}
                    onCheckedChange={(it) => field.handleChange(Boolean(it))}
                  />
                  <Label
                    htmlFor="terms"
                    className="text-muted-foreground text-sm"
                  >
                    {t('auth.completeProfile.termsAcknowledgement')}
                  </Label>
                </div>
                <FieldMessage errors={field.state.meta.errors} />
              </div>
            )}
          />

          <div className="text-center">
            <form.Subscribe
              selector={(state) => [state.canSubmit, state.isSubmitting]}
              children={([canSubmit, isSubmitting]) => (
                <Button type="submit" disabled={!canSubmit || isSubmitting}>
                  {isSubmitting
                    ? t('auth.common.processing')
                    : t('auth.common.continue')}
                </Button>
              )}
            />
          </div>

          <form.Subscribe
            selector={(state) => state.errors}
            children={(errors) => <FormMessage errors={errors} />}
          />
        </form>
      </CardContent>
    </AuthLayout>
  );
}
